/**
 * knz リファレンスの「はじめに（記法ガイド）」セクションのコンテンツ定義。
 * コンポーネント別リファレンスとは別に、記法そのものを解説する。
 */

export type GuideExample = {
  /** 見出し */
  heading: string;
  /** 本文（プレーン説明。改行区切り） */
  body: string[];
  /** knz ソース例（frontmatter なし本文。ガイドが frontmatter を補う。省略可） */
  source?: string;
  /** コードとして表示するだけでプレビューしないか（記法の説明用） */
  codeOnly?: boolean;
};

export type GuideTopic = {
  /** 左ツリー/右目次に出す id */
  id: string;
  /** タイトル */
  title: string;
  /** 概要 */
  summary: string;
  /** 節 */
  sections: GuideExample[];
};

export const GUIDE_TOPICS: GuideTopic[] = [
  {
    id: 'quickstart',
    title: 'クイックスタート',
    summary:
      '3 ステップで最初の 1 本を書けます。まず frontmatter、次に本文ブロック、最後にプレビューで確認します。',
    sections: [
      {
        heading: 'ステップ1: 新しい knz ファイルを作る',
        body: [
          'ワークスペースのエクスプローラーで新規ファイルを作り、拡張子を .knz にします（例: my-first.knz）。',
          '中身が空の knz ファイルは、title / author 入りのテンプレートで開きます。まずはそのまま保存できる状態です。',
        ],
      },
      {
        heading: 'ステップ2: frontmatter を書く',
        body: [
          'ファイルの先頭に --- で囲んだ frontmatter を書きます。title（記事タイトル）と author（あなたの userId）は必須です。',
          'author は「author:」の下に「- userId」を並べるリスト形式です（複数人ならこの下に追記）。1 人だけなら「author: userId」と 1 行で書くこともできます。',
          'title と author が無いとプレビューはエラーになります（保存は可能）。',
        ],
        source: ['---', 'title: はじめての記事', 'author:', ' - yamada', '---'].join('\n'),
        codeOnly: true,
      },
      {
        heading: 'ステップ3: 本文ブロックを足してプレビュー',
        body: [
          'frontmatter の下に、本文のブロックを書きます。まずは Text（段落）と Callout（注意ボックス）だけで十分です。',
          '書いたら Cmd(Ctrl)+P でプレビュー、Cmd(Ctrl)+\\ で左右分割にして、見た目を確認しながら書けます。',
          '下のソースをそのままコピーして貼ると、右のようにプレビューされます。',
        ],
        source: [
          '<--- Text --->',
          'text:',
          ' - これが最初の段落です。**太字** や `コード` も書けます。',
          '<--- !Text --->',
          '<--- Callout --->',
          'type:info',
          'text:',
          ' - 補足はこのボックスに書きます。',
          '<--- !Callout --->',
        ].join('\n'),
      },
      {
        heading: 'つまずきやすいポイント',
        body: [
          '開始タグ <--- 名前 ---> と閉じタグ <--- !名前 ---> は必ずペアにします（閉じ忘れはエラー）。',
          'Text や Callout の本文は text: の下の行を「 -」（半角スペース + ハイフン）で始めます。',
          '必須 param（例: Card の title、Section の title）が無いとエラーになります。赤い波線が出たらその行を確認してください。',
        ],
      },
    ],
  },
  {
    id: 'intro',
    title: 'knz とは',
    summary:
      'knz は Markdown 風の本文に独自コンポーネントを埋め込める記事フォーマットです。ブロック記法で書き、専用エディタで執筆・プレビューします。',
    sections: [
      {
        heading: 'ブロックの基本形',
        body: [
          'コンポーネントは <--- 名前 ---> で始まり <--- !名前 ---> で閉じます。',
          '中に key:value 形式で param（パラメータ）を 1 行ずつ書きます。',
        ],
        source: [
          '<--- Callout --->',
          'type:info',
          'text:',
          ' - これが基本のブロックです。',
          '<--- !Callout --->',
        ].join('\n'),
      },
      {
        heading: 'frontmatter（必須）',
        body: [
          'ファイルの先頭に --- で囲んだメタ情報（frontmatter）を必ず書きます。',
          'title（記事タイトル）と author（作成者の userId）が必須で、記事ヘッダーは frontmatter から自動生成されます。',
          'author の複数指定・副題（subtitle）・読み物モード（prose）などの詳しい書き方は、左メニューの「frontmatter（記事のメタ情報）」を参照してください。',
        ],
        source: [
          '---',
          'title: 記事タイトル',
          'author:',
          ' - yamada',
          '---',
          '<--- Text --->',
          'text:',
          ' - 本文です。',
          '<--- !Text --->',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'frontmatter',
    title: 'frontmatter（記事のメタ情報）',
    summary:
      'ファイル先頭の --- で囲んだ部分が frontmatter です。記事のタイトルや著者などのメタ情報を書きます。title と author は必須で、記事ヘッダーは frontmatter から自動生成されます。',
    sections: [
      {
        heading: '基本形（title / author は必須）',
        body: [
          'ファイルの先頭を --- で始め、もう一度 --- で閉じます。この 2 本の --- で挟まれた範囲が frontmatter です。',
          'title（記事タイトル）と author（作成者の userId）は必須です。どちらかが欠けているとプレビューはエラーになります（保存は可能）。',
          '書き方は「key: value」形式で、1 行に 1 項目です。ネストした複雑な YAML には対応していません。',
        ],
        source: [
          '---',
          'title: はじめての記事',
          'author:',
          ' - yamada',
          '---',
          '<--- Text --->',
          'text:',
          ' - frontmatter の下に本文を書きます。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: 'author の書き方（単数・複数・カンマ区切り）',
        body: [
          'author は 3 通りの書き方に対応しています。いずれも userId を指定します。',
          '(1) 1 人だけ: 「author: yamada」と 1 行で書けます。',
          '(2) 複数人: 「author:」の下に「- userId」を 1 行ずつ並べます（children と同じリスト記法。ハイフンの後は半角スペース）。',
          '(3) カンマ区切り: 「author: yamada, tanaka」のように 1 行にまとめても読めます。',
          '内部的には常に配列（authors: string[]）として扱われ、記事ヘッダーには全員の表示名が「、」で連結表示されます。',
        ],
        source: [
          '---',
          'title: 共著の記事',
          'author:',
          ' - yamada',
          ' - tanaka',
          '---',
          '<--- Text --->',
          'text:',
          ' - 複数人で書いた記事のヘッダーには全員が並びます。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: 'subtitle（副題・任意）',
        body: [
          'subtitle を指定すると、記事ヘッダーのタイトル下に副題が表示されます。任意項目なので省略できます。',
        ],
        source: [
          '---',
          'title: 記事タイトル',
          'author: yamada',
          'subtitle: 副題はここに表示されます',
          '---',
          '<--- Text --->',
          'text:',
          ' - タイトルの下に副題が出ます。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: 'description（説明・任意）',
        body: [
          'description は記事の説明文です。記事一覧のカードや記事フォームで使われます。任意項目なので省略できます。',
          '記事フォームを knz から開いたときは description がプレフィルされ、記事の公開・更新時にはフォームの説明が frontmatter の description に書き戻されます。',
          'ビューワーの描画には影響しません（記事機能が利用するメタ情報です）。',
        ],
        source: [
          '---',
          'title: 記事タイトル',
          'author: yamada',
          'description: この記事の概要をここに書きます',
          '---',
          '<--- Text --->',
          'text:',
          ' - description は本文には表示されませんが、記事一覧のカードに使われます。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: 'visible（公開設定・任意）',
        body: [
          'visible は記事の公開設定です。値は PUBLIC（公開）/ INTERNAL（社内公開）/ PRIVATE（非公開）のいずれかで、記事機能の公開設定（ArticleVisibility）に対応します。',
          '記事フォームを knz から開いたときは visible が公開設定にプレフィルされ、記事の公開・更新時にはフォームの公開設定が frontmatter の visible に書き戻されます。',
          'ビューワーの描画には影響しません。指定しない・不正な値のときはプレフィルで無視されます（記事フォーム側の既定値が使われます）。',
        ],
        source: [
          '---',
          'title: 記事タイトル',
          'author: yamada',
          'visible: INTERNAL',
          '---',
          '<--- Text --->',
          'text:',
          ' - visible は本文には表示されませんが、記事の公開範囲を表します。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: '読み物モード（prose / breadcrumb）',
        body: [
          'frontmatter に prose: true を足すと、本文が明朝体の紙面（新書風）で描画される「読み物モード」になります。',
          'breadcrumb（任意）は紙面上部に出るパンくずの親ラベルです。title / subtitle と合わせて紙面の見出しに使われます。',
          '読み物モードでは通常の記事ヘッダーは描画されず、タイトルは紙面側が担当します。本文は Chapter / ProseText などの読み物ブロックで構成します。',
          '完成例は .kiro/docs/thoughts_ai.knz を参照してください。',
        ],
        source: [
          '---',
          'title: 生成AIは道具か',
          'author:',
          ' - yamada',
          'prose: true',
          'subtitle: 認知機能の外部化についての考察',
          'breadcrumb: Dev Insights',
          '---',
          '<--- Chapter --->',
          'label:第一章',
          'title:文字、計算機、そして生成AI',
          'children:',
          ' - p1',
          '<--- !Chapter --->',
          '<--- ProseText --->',
          'id:p1',
          'text:',
          ' - 人類が最初に認知を外部へ預けたのは、文字の発明においてだった。',
          '<--- !ProseText --->',
        ].join('\n'),
      },
      {
        heading: 'つまずきやすいポイント',
        body: [
          '先頭行は必ず --- にします（前に空行やコメントがあると frontmatter として認識されません）。',
          '閉じの --- を書き忘れると「frontmatter が閉じられていません」というエラーになります。',
          'title と author のどちらかが欠けると「必須項目が不足しています」というエラーになります。',
          'author をリスト形式で書くときは「author:」の行の値を空にして、次の行から「- userId」を並べます（同じ行に値とリストは併記しません）。',
        ],
        codeOnly: true,
      },
    ],
  },
  {
    id: 'text-syntax',
    title: 'テキストの書き方',
    summary:
      'text param の書き方は 3 種類（配列・改行結合・生テキスト）あり、コンポーネントごとに決まっています。',
    sections: [
      {
        heading: '複数行テキスト（joined）',
        body: [
          'Callout や Text などは text: の下に「- 行」（ハイフン + 半角スペース + 内容）を並べます。改行結合され Markdown として解釈されます。',
          '内容のない「-」だけの行は空行（段落区切り）になります。',
        ],
        source: [
          '<--- Text --->',
          'text:',
          ' - 1段落目。**太字** や `code` が使えます。',
          ' -',
          ' - 空行をはさむと段落が分かれます。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: 'リスト（array）',
        body: ['List や Checklist は「- 項目」（ハイフン + 半角スペース）が各要素になります。'],
        source: [
          '<--- List --->',
          'variant:unordered',
          'text:',
          ' - りんご',
          ' - みかん',
          '<--- !List --->',
        ].join('\n'),
      },
      {
        heading: '生テキスト（raw）',
        body: [
          'CodeBlock や Mermaid は text をそのまま（無加工で）取り込みます。',
          '「 -」接頭辞は不要で、コードや図の定義をそのまま貼れます。text は最後の param にします。',
        ],
        source: [
          '<--- CodeBlock --->',
          'language:ts',
          'text:',
          'const a = 1;',
          'const b = a - 1;',
          '<--- !CodeBlock --->',
        ].join('\n'),
      },
      {
        heading: 'ファイル参照（CodeBlock の link）',
        body: [
          'CodeBlock に link を指定すると、マイワークスペースのファイル内容を動的に表示できます。',
          'link: の後で補完を開くと、マイワークスペースの全ファイルが候補に出ます（ファイル名で表示、サブにディレクトリ）。選ぶと fileId が埋め込まれます。',
          'fileId で取得したファイル名が title に、内容が text に、拡張子が language に自動設定されます。表示は取得時点の内容で、参照先ファイルを編集すればプレビューを開き直したときに反映されます。',
          'param で title / text / language を明示した場合は、その値が取得情報より優先されます。',
          '参照先が見つからない場合はエラーにならず、「参照先のファイルが存在しません」と表示されます。',
        ],
        codeOnly: true,
        source: [
          '<--- CodeBlock --->',
          'link:（補完で選んだ fileId）',
          '<--- !CodeBlock --->',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'nesting',
    title: '入れ子（children）',
    summary:
      'ブロックの中に別のブロックを入れたいときは、id を付けて children で参照します。Tabs や Grid、Section はこの仕組みで組み合わせます。',
    sections: [
      {
        heading: 'children で入れ子を作る',
        body: [
          '子ブロックに id を付け、親ブロックの children に「- id」（ハイフン + 半角スペース + id）を1行ずつ書いて参照します（推奨）。',
          'children の行で「-」を打つと、ファイル内の id が候補として補完されます（選ぶと「- id」の形で挿入されます。Ctrl+Space でも表示）。',
          'カンマ区切り（children:a,b）も読めますが、見づらいため非推奨です。',
          'ハイフンの直後は半角スペースを入れて「- id」と書くのが推奨です。「-id」（スペースなし）でも動きますが、警告（黄波線）が出ます。',
          'children から参照された子は、その場（トップレベル）には描画されず、親の中に描画されます。',
        ],
        source: [
          '<--- Section --->',
          'title:使い方',
          'number:1',
          'children:',
          ' - step1',
          '<--- !Section --->',
          '<--- Text --->',
          'id:step1',
          'text:',
          ' - セクションの中に入る本文です。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: '多段の入れ子（Tabs → Tab → 中身）',
        body: [
          'Tabs は children で Tab を参照し、その Tab がさらに children で中身（CodeBlock など）を参照できます。',
          'このように id 参照をたどることで、何段でも入れ子にできます。',
          'ブロックの記述順は自由です（親より子を先に書いても、id で正しく解決されます）。',
        ],
        source: [
          '<--- Tabs --->',
          'children:',
          ' - tab-a',
          ' - tab-b',
          '<--- !Tabs --->',
          '<--- Tab --->',
          'id:tab-a',
          'label:概要',
          'text:',
          ' - Tab の中に本文を直接書けます。',
          '<--- !Tab --->',
          '<--- Tab --->',
          'id:tab-b',
          'label:コード',
          'children:',
          ' - code-b',
          '<--- !Tab --->',
          '<--- CodeBlock --->',
          'id:code-b',
          'language:ts',
          'text:',
          'const x = 1;',
          '<--- !CodeBlock --->',
        ].join('\n'),
      },
      {
        heading: '入れ子で使うコンポーネント',
        body: [
          'Tabs（children で Tab を参照）／ Grid（children でカード等を並べる）／ Section（children で本文を持つ）が代表例です。',
          '子として使う Tab には必ず label を付けます（タブ見出しになります）。',
        ],
      },
    ],
  },
  {
    id: 'inline',
    title: 'インライン要素',
    summary: 'text の途中に {{...}} でキーボードキー・バッジ・ユーザーメンションを埋め込めます。',
    sections: [
      {
        heading: 'インライントークン',
        body: [
          '{{kbd:Ctrl+S}} でキー表示、{{badge:重要}} でバッジ、{{mention:userId}} または @userId でメンションになります。',
        ],
        source: [
          '<--- Text --->',
          'text:',
          ' - 保存は {{kbd:Ctrl+S}} 、担当は @yamada 、状態は {{badge:実験中}} です。',
          '<--- !Text --->',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'comment',
    title: 'コメント',
    summary:
      '行頭を // で始めると、その行はコメントになりプレビューには表示されません。メモや下書きに使えます。',
    sections: [
      {
        heading: 'コメント行',
        body: [
          '行頭（前に空白のみ可）が // の行はコメントとして無視され、プレビューに出ません。',
          'エディタでは Cmd(Ctrl)+/ で選択行のコメントをまとめて切り替えられます。',
        ],
        source: [
          '// これはコメント（表示されない）',
          '<--- Text --->',
          'text:',
          ' - 本文はここに書く',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        heading: '注意: コード/図の中の // はコメントにならない',
        body: [
          'CodeBlock や Mermaid の本文（raw）に書いた // は、コードの一部として扱われコメントにはなりません。',
          'コメントとして無視されるのは、ブロックの外や param 行のレベルにある行頭 // だけです。',
        ],
        source: [
          '<--- CodeBlock --->',
          'language:ts',
          'text:',
          '// これはコードのコメント（プレビューにも表示される）',
          'const x = 1;',
          '<--- !CodeBlock --->',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'errors',
    title: 'エラーと下書き',
    summary:
      'エディタは構文エラーを波線で示します。エラーがあっても保存はできますが、プレビューは表示されません。',
    sections: [
      {
        heading: 'エラーの扱い',
        body: [
          'frontmatter の欠落・必須 param の不足・閉じ忘れ・タグの入れ子・循環参照は「エラー（赤波線）」です。',
          '未定義 id の参照・id の重複・登録外のブロック名・ブロック外のテキスト・リスト項目のスペース抜け（`-項目`）は「警告（黄波線）」で、該当箇所だけ無視して残りは表示されます。',
          'エラーが 1 件でもある記事はプレビュー（ビューワー）では表示されません。警告だけなら、その箇所を無視して全体が表示されます。',
          'エラーがあっても下書きとして保存はできます（保存とプレビューで扱いが異なります）。',
        ],
        codeOnly: true,
      },
    ],
  },
];
